import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import {
  AlertsAndNotificationsState,
  AlertsAndNotification,
  AlertsAndNotificationsResponse,
  MomentNotificationsResponse,
  isAlert,
  isNotification
} from '../types';
import { alertsAndNotificationsSaga } from './saga';

export const initialState: AlertsAndNotificationsState = {
  loading: false,
  error: null,
  data: [],
  alerts: [],
  notifications: [],
  alertsByIssueId: {},
  notificationsById: {},
  momentAlertsByIssueId: {},
  clearedFieldRefs: {},
  alertCountByIssueId: {},
  overdueAlerts: []
};

const alertsAndNotificationsSlice = createSlice({
  name: 'alertsAndNotifications',
  initialState,
  reducers: {
    loadAlertsAndNotifications(state) {
      state.loading = true;
      state.error = null;
    },
    alertsAndNotificationsLoaded(
      state,
      action: PayloadAction<AlertsAndNotificationsResponse>
    ) {
      state.loading = false;
      const allItems = action.payload;
      state.data = allItems;

      // Separate alerts and notifications
      const alerts: AlertsAndNotification[] = [];
      const notifications: AlertsAndNotification[] = [];
      const alertsByIssueId: Record<string, AlertsAndNotification> = {};
      const notificationsById: Record<string, AlertsAndNotification> = {};

      allItems.forEach(item => {
        if (isAlert(item)) {
          alerts.push(item);
          alertsByIssueId[item.issueId] = item;
        } else if (isNotification(item)) {
          notifications.push(item);
          notificationsById[item.id] = item;
        }
      });

      state.alerts = alerts;
      state.notifications = notifications;
      state.alertsByIssueId = alertsByIssueId;
      state.notificationsById = notificationsById;
      // Do not touch momentAlertsByIssueId — slideout uses moment API only
    },
    alertsAndNotificationsError(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },
    clearFieldHighlight(
      state,
      action: PayloadAction<{ issueId: string; fieldRef: string }>
    ) {
      const cleared = state.clearedFieldRefs[action.payload.issueId] || [];
      // Add to cleared list if not already there
      if (!cleared.includes(action.payload.fieldRef)) {
        state.clearedFieldRefs[action.payload.issueId] = [
          ...cleared,
          action.payload.fieldRef
        ];
      }
    },
    clearAllHighlights(state, action: PayloadAction<string>) {
      delete state.clearedFieldRefs[action.payload];
    },
    dismissAlert(state, action: PayloadAction<string>) {
      // Remove alert by id from all arrays and maps
      const alertId = action.payload;

      // Find the item first before removing (to check if it's alert or notification)
      const itemToRemove = state.data.find(item => item.id === alertId);

      if (itemToRemove) {
        // Remove from alertsByIssueId map if it's an alert
        if (isAlert(itemToRemove)) {
          delete state.alertsByIssueId[itemToRemove.issueId];
          delete state.momentAlertsByIssueId[itemToRemove.issueId];
        }

        // Remove from notificationsById map if it's a notification
        if (isNotification(itemToRemove)) {
          delete state.notificationsById[itemToRemove.id];
        }
      }

      // Remove from data array
      state.data = state.data.filter(item => item.id !== alertId);

      // Remove from alerts array
      state.alerts = state.alerts.filter(alert => alert.id !== alertId);

      // Remove from notifications array
      state.notifications = state.notifications.filter(
        notification => notification.id !== alertId
      );
    },
    // Moment-specific notifications actions
    loadMomentNotifications(state, action: PayloadAction<{ issueId: string }>) {
      // Loading state is handled in saga, but we can track per-issue loading if needed
      state.error = null;
    },
    momentNotificationsLoaded(
      state,
      action: PayloadAction<{
        issueId: string;
        response: MomentNotificationsResponse;
      }>
    ) {
      const { issueId, response } = action.payload;
      if (response.syncStatus === 'STALE') return;
      delete state.clearedFieldRefs[issueId];
      const momentNotifications = response.notifications || [];

      // Always sync badge count from API (must run for empty arrays too — early return used to skip this)
      state.alertCountByIssueId[issueId] = response.alertCount ?? 0;

      // The response is the full moment-scoped snapshot: an alert that is absent from it has been
      // resolved, so drop it (do not mutate banner list / data)
      if (
        !momentNotifications.some(
          item => isAlert(item) && item.issueId === issueId
        )
      ) {
        delete state.momentAlertsByIssueId[issueId];
      }

      momentNotifications.forEach(item => {
        if (isAlert(item) && item.issueId === issueId) {
          state.momentAlertsByIssueId[issueId] = item;
        } else if (isNotification(item)) {
          // Update notifications
          state.notificationsById[item.id] = item;

          const existingIndex = state.data.findIndex(d => d.id === item.id);
          if (existingIndex >= 0) {
            state.data[existingIndex] = item;
          } else {
            state.data.push(item);
          }

          const notificationIndex = state.notifications.findIndex(
            n => n.id === item.id
          );
          if (notificationIndex >= 0) {
            state.notifications[notificationIndex] = item;
          } else {
            state.notifications.push(item);
          }
        }
      });
    },
    momentNotificationsError(
      state,
      action: PayloadAction<{ issueId: string; error: any }>
    ) {
      // Log error but don't block other functionality
      state.error = action.payload.error;
    },
    /**
     * Replace the set of client-side computed overdue-feature alerts. Stored under a dedicated
     * slice key so it never collides with server-loaded alerts. Pass an empty array to clear
     * (e.g. when a feature is no longer overdue or the source data is unavailable).
     * Deliberately NOT touched by `dismissAlert` — overdue alerts are re-derived from roadmap
     * data, not dismissed.
     */
    setOverdueAlerts(state, action: PayloadAction<AlertsAndNotification[]>) {
      state.overdueAlerts = action.payload;
    }
  }
});

export const { actions, reducer } = alertsAndNotificationsSlice;

// Hook to inject the slice into the main Redux store
export const useAlertsAndNotificationsSlice = () => {
  useInjectReducer({
    key: alertsAndNotificationsSlice.name,
    reducer: alertsAndNotificationsSlice.reducer
  });
  useInjectSaga({
    key: alertsAndNotificationsSlice.name,
    saga: alertsAndNotificationsSaga
  });
  return { actions: alertsAndNotificationsSlice.actions };
};

export default alertsAndNotificationsSlice;
